import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { User, Save, Loader2, Key, Link2, Copy, Check, DollarSign, Star, Trophy, ArrowUp, History, Zap, Share2, Gift, Bell, BellOff, Smartphone, Package, Truck, ChevronRight, ExternalLink, Music2, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../App";
import { toast } from "sonner";
import { initPushNotifications, requestNotificationPermission } from "../utils/pushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierColors = {
  fumes: { bg: "bg-zinc-500/20", text: "text-zinc-400", border: "border-zinc-500/50" },
  unleaded: { bg: "bg-neon-cyan/20", text: "text-neon-cyan", border: "border-neon-cyan/50" },
  premium: { bg: "bg-neon-magenta/20", text: "text-neon-magenta", border: "border-neon-magenta/50" },
  nitrous: { bg: "bg-neon-yellow/20", text: "text-neon-yellow", border: "border-neon-yellow/50" }
};

const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nameVal, setNameVal] = useState("");
  const [emailVal, setEmailVal] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [affiliate, setAffiliate] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralStats, setReferralStats] = useState(null);
  const [gcBalance, setGcBalance] = useState(0);
  const [refCopied, setRefCopied] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ wishlist_alerts: true, new_drops: true, restock_alerts: true, flash_sale_alerts: true });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchProfile();
    fetchAffiliate();
    fetchLoyalty();
    fetchReferral();
    fetchGCBalance();
    fetchNotifPrefs();
    fetchRecentOrders();
    // Check if push is already enabled
    if ('Notification' in window && Notification.permission === 'granted') {
      setPushEnabled(true);
    }
  }, [user]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/profile`, { headers: getAuthHeaders(), withCredentials: true });
      setProfile(res.data);
      setNameVal(res.data.name || "");
      setEmailVal(res.data.email || "");
    } catch (e) {} finally { setLoading(false); }
  };

  const fetchAffiliate = async () => {
    try {
      const res = await axios.get(`${API}/affiliate/me`, { headers: getAuthHeaders(), withCredentials: true });
      if (res.data.enrolled) setAffiliate(res.data);
    } catch (e) {}
  };

  const fetchLoyalty = async () => {
    try {
      const [loyaltyRes, historyRes] = await Promise.all([
        axios.get(`${API}/loyalty/me`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/loyalty/history`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      setLoyalty(loyaltyRes.data);
      setPointsHistory(historyRes.data);
    } catch (e) {}
  };

  const fetchReferral = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/referral/stats`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      setReferralCode(codeRes.data.referral_code);
      setReferralStats(statsRes.data);
    } catch (e) {}
  };

  const fetchGCBalance = async () => {
    try {
      const res = await axios.get(`${API}/gift-cards/balance`, { headers: getAuthHeaders(), withCredentials: true });
      setGcBalance(res.data.balance || 0);
    } catch (e) {}
  };

  const handleCopyReferralLink = async () => {
    const link = `${window.location.origin}?ref=${referralCode}`;
    await navigator.clipboard.writeText(link);
    setRefCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setRefCopied(false), 2000);
  };

  const fetchNotifPrefs = async () => {
    try {
      const res = await axios.get(`${API}/notifications/preferences`, { headers: getAuthHeaders(), withCredentials: true });
      setNotifPrefs(res.data);
    } catch (e) {}
  };

  const fetchRecentOrders = async () => {
    try {
      const res = await axios.get(`${API}/my-orders`, { headers: getAuthHeaders(), withCredentials: true });
      setRecentOrders((res.data || []).slice(0, 3));
    } catch (e) {}
  };

  const toggleNotifPref = async (key) => {
    const newVal = !notifPrefs[key];
    setNotifPrefs(prev => ({ ...prev, [key]: newVal }));
    try {
      await axios.put(`${API}/notifications/preferences`, { [key]: newVal }, { headers: getAuthHeaders(), withCredentials: true });
    } catch (e) {
      setNotifPrefs(prev => ({ ...prev, [key]: !newVal }));
      toast.error("Failed to update preferences");
    }
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      const result = await initPushNotifications();
      if (result) {
        setPushEnabled(true);
        toast.success("Push notifications enabled!");
      } else {
        const { reason } = await requestNotificationPermission();
        if (reason === 'denied') {
          toast.error("Notifications blocked. Enable them in your browser settings.");
        } else if (reason === 'not-supported') {
          toast.error("Your browser doesn't support push notifications.");
        }
      }
    } catch (e) {
      toast.error("Failed to enable push notifications");
    } finally {
      setPushLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (nameVal !== profile.name) payload.name = nameVal;
      if (emailVal !== profile.email) payload.email = emailVal;
      if (newPw) { payload.current_password = currentPw; payload.new_password = newPw; }
      if (Object.keys(payload).length === 0) { toast.info("No changes"); setSaving(false); return; }
      await axios.put(`${API}/profile`, payload, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Profile updated!");
      setCurrentPw(""); setNewPw("");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally { setSaving(false); }
  };

  const handleEnrollAffiliate = async () => {
    setEnrolling(true);
    try {
      const res = await axios.post(`${API}/affiliate/enroll`, {}, { headers: getAuthHeaders(), withCredentials: true });
      setAffiliate(res.data);
      toast.success("You're now an affiliate!");
    } catch (e) { toast.error("Failed to enroll"); } finally { setEnrolling(false); }
  };

  const handleCopyLink = async () => {
    const link = `${window.location.origin}?ref=${affiliate.referral_code}`;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user) {
    return (
      <div className="min-h-screen py-10 px-6" data-testid="profile-page">
        <div className="max-w-2xl mx-auto text-center py-20">
          <User className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="font-heading text-3xl uppercase mb-4">Profile</h1>
          <p className="text-zinc-400 mb-6">Please login to view your profile</p>
          <Button onClick={() => navigate("/")} className="bg-neon-cyan text-black">Go Home</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-neon-cyan border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tc = loyalty ? tierColors[loyalty.tier?.tier] || tierColors.fumes : tierColors.fumes;
  const tierProgress = loyalty?.next_tier
    ? ((loyalty.lifetime_points - (loyalty.tier?.min_points || 0)) / (loyalty.next_tier.min_points - (loyalty.tier?.min_points || 0))) * 100
    : 100;

  return (
    <div className="min-h-screen py-10 px-6" data-testid="profile-page">
      <div className="max-w-2xl mx-auto">
        <Badge className="bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50 mb-4">Account</Badge>
        <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-8">Profile</h1>

        {/* Octane Rewards Card */}
        {loyalty && (
          <div className="glass p-6 mb-6" data-testid="loyalty-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase flex items-center gap-2">
                <Trophy className="w-5 h-5 text-neon-yellow" /> Octane Rewards
              </h2>
              <Badge className={`${tc.bg} ${tc.text} ${tc.border} uppercase font-bold tracking-wider`} data-testid="tier-badge">
                {loyalty.tier?.label}
              </Badge>
            </div>

            {/* Octane Display */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white/5 p-4 text-center rounded-sm">
                <p className="font-heading text-3xl text-neon-yellow" data-testid="current-points">{loyalty.current_points?.toLocaleString()}</p>
                <p className="text-xs text-zinc-500 uppercase mt-1">Available Octane</p>
              </div>
              <div className="bg-white/5 p-4 text-center rounded-sm">
                <p className="font-heading text-3xl text-neon-green">${loyalty.points_value?.toFixed(2)}</p>
                <p className="text-xs text-zinc-500 uppercase mt-1">Octane Value</p>
              </div>
              <div className="bg-white/5 p-4 text-center rounded-sm">
                <p className="font-heading text-3xl text-neon-cyan">{loyalty.lifetime_points?.toLocaleString()}</p>
                <p className="text-xs text-zinc-500 uppercase mt-1">Lifetime Octane</p>
              </div>
            </div>

            {/* Current Multiplier */}
            {loyalty.active_multiplier > 1 && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-neon-yellow/10 border border-neon-yellow/30 rounded-sm">
                <Zap className="w-4 h-4 text-neon-yellow" />
                <span className="text-sm text-neon-yellow font-bold">{loyalty.active_multiplier}x Octane Multiplier Active!</span>
              </div>
            )}

            {/* Tier Progress */}
            {loyalty.next_tier && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-zinc-500 mb-2">
                  <span>{loyalty.tier?.label}</span>
                  <span>{loyalty.next_tier.points_needed?.toLocaleString()} Octane to {loyalty.next_tier.label}</span>
                </div>
                <Progress value={Math.min(tierProgress, 100)} className="h-2" />
              </div>
            )}

            {/* Tier Info */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[
                { key: "fumes", name: "Fumes", pts: "0+", mult: "1x" },
                { key: "unleaded", name: "Unleaded", pts: "1K+", mult: "1.25x" },
                { key: "premium", name: "Premium", pts: "5K+", mult: "1.5x" },
                { key: "nitrous", name: "Nitrous", pts: "15K+", mult: "2x" }
              ].map(t => (
                <div key={t.key} className={`p-3 text-center border rounded-sm ${
                  loyalty.tier?.tier === t.key ? `${tierColors[t.key]?.border} ${tierColors[t.key]?.bg}` : 'border-white/5 opacity-50'
                }`}>
                  <p className={`text-xs font-bold uppercase ${loyalty.tier?.tier === t.key ? tierColors[t.key]?.text : 'text-zinc-500'}`}>{t.name}</p>
                  <p className="text-xs text-zinc-600 mt-1">{t.pts}</p>
                  <p className="text-xs text-zinc-600">{t.mult}</p>
                </div>
              ))}
            </div>

            <p className="text-xs text-zinc-600">
              Earn {loyalty.octane_per_dollar || loyalty.points_per_dollar} Octane per $1 spent. {loyalty.octane_to_dollar || loyalty.points_to_dollar} Octane = $1 discount at checkout.
            </p>

            {/* Octane History Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="mt-4 text-zinc-400 text-xs"
              data-testid="toggle-history-button"
            >
              <History className="w-3 h-3 mr-1" /> {showHistory ? 'Hide' : 'Show'} Octane History
            </Button>

            {showHistory && (
              <div className="mt-3 space-y-2 max-h-60 overflow-y-auto" data-testid="points-history">
                {pointsHistory.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-4">No Octane activity yet</p>
                ) : (
                  pointsHistory.map((h) => (
                    <div key={h.history_id} className="flex items-center justify-between p-3 bg-white/5 rounded-sm text-sm">
                      <div>
                        <p className="text-zinc-300">{h.reason}</p>
                        <p className="text-xs text-zinc-600">{new Date(h.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`font-mono font-bold ${h.points > 0 ? 'text-neon-green' : 'text-red-400'}`}>
                        {h.points > 0 ? '+' : ''}{h.points} OCT
                        {h.multiplier > 1 && <span className="text-xs text-zinc-500 ml-1">({h.multiplier}x)</span>}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Profile Form */}
        <form onSubmit={handleSave} className="glass p-6 mb-6 space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={nameVal} onChange={(e) => setNameVal(e.target.value)} className="bg-black/50 border-white/10" data-testid="profile-name-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={emailVal} onChange={(e) => setEmailVal(e.target.value)} className="bg-black/50 border-white/10" data-testid="profile-email-input" />
          </div>
          <div className="border-t border-white/10 pt-4">
            <h3 className="font-medium mb-3 flex items-center gap-2"><Key className="w-4 h-4" /> Change Password</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Current Password</Label>
                <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="bg-black/50 border-white/10" data-testid="profile-current-pw" />
              </div>
              <div>
                <Label>New Password</Label>
                <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-black/50 border-white/10" data-testid="profile-new-pw" />
              </div>
            </div>
          </div>
          <Button type="submit" disabled={saving} className="bg-neon-cyan text-black font-bold w-full" data-testid="save-profile-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </form>

        {/* Affiliate Section */}
        <div className="glass p-6" data-testid="affiliate-section">
          <h2 className="font-heading text-xl uppercase mb-4">Affiliate Program</h2>
          {affiliate ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">Share your referral link and earn <span className="text-neon-green font-bold">{affiliate.commission_rate}%</span> commission on every sale!</p>
              <div className="flex gap-2">
                <Input value={`${window.location.origin}?ref=${affiliate.referral_code}`} readOnly className="bg-black/50 border-white/10 font-mono text-sm" data-testid="referral-link" />
                <Button onClick={handleCopyLink} variant="outline" className="border-neon-cyan text-neon-cyan flex-shrink-0" data-testid="copy-referral-link">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="bg-white/5 p-4 text-center">
                  <p className="font-heading text-2xl text-neon-cyan">{affiliate.total_referrals}</p>
                  <p className="text-xs text-zinc-500 uppercase">Referrals</p>
                </div>
                <div className="bg-white/5 p-4 text-center">
                  <p className="font-heading text-2xl text-neon-green">${affiliate.total_earnings.toFixed(2)}</p>
                  <p className="text-xs text-zinc-500 uppercase">Earned</p>
                </div>
                <div className="bg-white/5 p-4 text-center">
                  <p className="font-heading text-2xl text-neon-yellow">${affiliate.pending_earnings.toFixed(2)}</p>
                  <p className="text-xs text-zinc-500 uppercase">Pending</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <DollarSign className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 mb-4">Earn 10% commission by referring customers</p>
              <Button onClick={handleEnrollAffiliate} disabled={enrolling} className="bg-neon-green text-black font-bold" data-testid="enroll-affiliate-button">
                {enrolling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                Join Affiliate Program
              </Button>
            </div>
          )}
        </div>

        {/* Referral Invite Section */}
        <div className="glass p-6 mt-6" data-testid="referral-section">
          <h2 className="font-heading text-xl uppercase mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-neon-cyan" /> Invite Friends
          </h2>
          <p className="text-sm text-zinc-400 mb-4">
            Share your referral link — both you and your friend get <span className="text-neon-green font-bold">15% off</span> your next purchase!
          </p>
          {referralCode && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={`${window.location.origin}?ref=${referralCode}`}
                  readOnly
                  className="bg-black/50 border-white/10 font-mono text-sm"
                  data-testid="referral-invite-link"
                />
                <Button onClick={handleCopyReferralLink} variant="outline" className="border-neon-cyan text-neon-cyan flex-shrink-0" data-testid="copy-referral-invite">
                  {refCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              {referralStats && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 p-4 text-center">
                    <p className="font-heading text-2xl text-neon-cyan">{referralStats.total_referrals}</p>
                    <p className="text-xs text-zinc-500 uppercase">Friends Referred</p>
                  </div>
                  <div className="bg-white/5 p-4 text-center">
                    <p className="font-heading text-2xl text-neon-green">{referralStats.reward_codes?.length || 0}</p>
                    <p className="text-xs text-zinc-500 uppercase">Rewards Earned</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Gift Card Balance */}
        {gcBalance > 0 && (
          <div className="glass p-6 mt-6" data-testid="gc-balance-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 text-yellow-400" />
                <h2 className="font-heading text-xl uppercase">Gift Card Balance</h2>
              </div>
              <span className="font-heading text-2xl text-yellow-400">${gcBalance.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Recent Orders */}
        <div className="glass p-6 mt-6" data-testid="recent-orders-section">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl uppercase flex items-center gap-2">
              <Package className="w-5 h-5 text-neon-cyan" /> Recent Orders
            </h2>
            <a href="/orders" className="text-xs font-heading uppercase tracking-widest text-neon-cyan hover:text-white transition-colors flex items-center gap-1" data-testid="view-all-orders-link">
              View All <ChevronRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {recentOrders.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No orders yet</p>
              <div className="flex gap-2 justify-center mt-4 flex-wrap">
                <a href="/beats"><Button size="sm" className="bg-neon-cyan text-black text-xs font-bold uppercase">Browse Beats</Button></a>
                <a href="/merch"><Button size="sm" variant="outline" className="border-white/20 text-xs font-bold uppercase">Shop Merch</Button></a>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {recentOrders.map(order => {
                const hasMerch = order.items?.some(i => i.item_type === "merch");
                const tracking = order.tracking_info;
                const statusConfig = (() => {
                  if (tracking?.delivered_at) return { label: "Delivered", color: "bg-neon-green/20 text-neon-green border-neon-green/30" };
                  if (tracking?.tracking_number) return { label: "Shipped", color: "bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30" };
                  if (order.fulfillment_status === "in_production") return { label: "In Production", color: "bg-neon-magenta/20 text-neon-magenta border-neon-magenta/30" };
                  if (order.printify_order_id) return { label: "At Printify", color: "bg-neon-yellow/20 text-neon-yellow border-neon-yellow/30" };
                  if (order.status === "completed") return { label: "Completed", color: "bg-neon-green/20 text-neon-green border-neon-green/30" };
                  return { label: "Processing", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" };
                })();

                return (
                  <a href="/orders" key={order.order_id}
                    className="flex items-center gap-4 p-4 border border-white/10 bg-zinc-950/60 hover:border-white/25 hover:bg-zinc-900/60 transition-all group"
                    data-testid={`recent-order-${order.order_id}`}>
                    {/* First item image */}
                    <div className="flex-shrink-0">
                      {order.items?.[0]?.image
                        ? <img src={order.items[0].image} alt="" className="w-12 h-12 object-cover" />
                        : <div className="w-12 h-12 bg-zinc-900 flex items-center justify-center border border-white/10">
                            {hasMerch ? <Package className="w-5 h-5 text-zinc-600" /> : <Music2 className="w-5 h-5 text-zinc-600" />}
                          </div>
                      }
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {order.items?.map(i => i.name || i.item_id).slice(0, 2).join(", ")}
                        {(order.items?.length || 0) > 2 && ` +${order.items.length - 2} more`}
                      </p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className={`text-xs border ${statusConfig.color}`}>{statusConfig.label}</Badge>
                        {tracking?.tracking_number && (
                          <span className="font-mono text-xs text-zinc-500">{tracking.carrier} · {tracking.tracking_number}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0 text-right">
                      <p className="font-heading text-neon-cyan text-sm">${order.total?.toFixed(2)}</p>
                      <p className="text-xs text-zinc-600 mt-0.5">
                        {new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </p>
                    </div>

                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-300 transition-colors flex-shrink-0" />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Notification Preferences */}
        <div className="glass p-6 mt-6" data-testid="notification-prefs">
          <h2 className="font-heading text-xl uppercase mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-neon-magenta" /> Notification Preferences
          </h2>

          {/* Push Notification Enable */}
          <div className="mb-5 p-4 border border-white/10 bg-white/5 flex items-center justify-between" data-testid="push-notification-toggle">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-neon-cyan" />
              <div>
                <p className="text-sm font-medium">Browser Push Notifications</p>
                <p className="text-xs text-zinc-500">Get real-time alerts even when the tab is closed</p>
              </div>
            </div>
            {pushEnabled ? (
              <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50">Enabled</Badge>
            ) : (
              <Button
                size="sm"
                onClick={handleEnablePush}
                disabled={pushLoading}
                className="bg-neon-cyan text-black font-bold text-xs"
                data-testid="enable-push-btn"
              >
                {pushLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
                Enable
              </Button>
            )}
          </div>

          <p className="text-sm text-zinc-500 mb-4">Choose what alerts you want to receive.</p>
          <div className="space-y-3">
            {[
              { key: "wishlist_alerts", label: "Wishlist Alerts", desc: "Get notified when wishlisted items go on sale" },
              { key: "new_drops", label: "New Drop Alerts", desc: "Be first to know about new beats and merch" },
              { key: "restock_alerts", label: "Restock Alerts", desc: "Get notified when out-of-stock items return" },
              { key: "flash_sale_alerts", label: "Flash Sale Alerts", desc: "Never miss a flash sale" },
              { key: "order_updates", label: "Order Updates", desc: "Get notified about order status changes" },
              { key: "contest_updates", label: "Contest Updates", desc: "Stay updated on beat contests and results" },
            ].map(({ key, label, desc }) => (
              <button
                key={key}
                onClick={() => toggleNotifPref(key)}
                className={`w-full flex items-center justify-between p-3 border transition-all ${
                  notifPrefs[key]
                    ? "border-neon-magenta/30 bg-neon-magenta/5"
                    : "border-white/5 bg-transparent"
                }`}
                data-testid={`notif-toggle-${key}`}
              >
                <div className="text-left">
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-zinc-500">{desc}</p>
                </div>
                <div className={`w-10 h-6 rounded-full relative transition-colors ${notifPrefs[key] ? "bg-neon-magenta" : "bg-zinc-700"}`}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs[key] ? "left-5" : "left-1"}`} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
